import { useNavigate } from 'react-router-dom';
import TableOfContents from './TableOfContents';
import type { ArticlePost } from '@/lib/articles';

interface Props {
  currentPost: ArticlePost;
  posts: ArticlePost[];
}

const BlogDetailSidebar = ({ currentPost, posts }: Props) => {
  const navigate = useNavigate();
  const related = (() => {
    const sameCategory = posts.filter((post) => post.id !== currentPost.id && post.category === currentPost.category);
    const withSharedTags = posts.filter(
      (post) => post.id !== currentPost.id && post.category !== currentPost.category && post.tags.some((tag) => currentPost.tags.includes(tag))
    );
    const rest = posts.filter(
      (post) => post.id !== currentPost.id && !sameCategory.find((item) => item.id === post.id) && !withSharedTags.find((item) => item.id === post.id)
    );
    return [...sameCategory, ...withSharedTags, ...rest].slice(0, 4);
  })();
  const popular = [...posts].sort((a, b) => b.views - a.views).filter((post) => post.id !== currentPost.id).slice(0, 3);
  const tags = Array.from(new Set(posts.flatMap((post) => post.tags))).slice(0, 12);

  return (
    <div className="space-y-6">
      <TableOfContents post={currentPost} />

      <div className={`bg-gradient-to-br ${currentPost.gradient} rounded-2xl p-6 text-white`}>
        <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-4">
          <i className="ri-rocket-line text-white text-lg" />
        </div>
        <h3 className="font-bold text-base mb-2 leading-snug">
          想和文章作者
          <br />
          一對一深聊？
        </h3>
        <p className="text-white/80 text-xs leading-relaxed mb-4">
          在 NexusGo 預約來自 {currentPost.authorCompany} 的導師，獲取專屬職業規劃建議。
        </p>
        <button
          onClick={() => navigate('/mentors')}
          className="w-full py-2.5 bg-white text-[#0A1628] text-xs font-bold rounded-xl cursor-pointer hover:bg-white/90 transition-colors whitespace-nowrap"
        >
          預約導師諮詢 →
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h4 className="text-sm font-bold text-[#0A1628] mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-fire-line text-orange-500" />
          </div>
          本週最熱
        </h4>
        <div className="space-y-1">
          {popular.map((post, idx) => (
            <button
              key={post.id}
              onClick={() => navigate(`/blog/${post.id}`)}
              className="w-full text-left flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group"
            >
              <span className={`text-lg font-black flex-shrink-0 leading-none mt-0.5 ${idx === 0 ? 'text-orange-500' : idx === 1 ? 'text-slate-400' : 'text-slate-300'}`}>
                {idx + 1}
              </span>
              <div>
                <p className="text-xs font-semibold text-[#0A1628] leading-snug line-clamp-2 group-hover:text-violet-700 transition-colors">
                  {post.title}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">{(post.views / 1000).toFixed(1)}K 次閱讀</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h4 className="text-sm font-bold text-[#0A1628] mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-links-line text-violet-500" />
          </div>
          相關推薦
        </h4>
        <div className="space-y-1">
          {related.map((post) => (
            <button
              key={post.id}
              onClick={() => navigate(`/blog/${post.id}`)}
              className="w-full text-left flex gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group"
            >
              <div className="w-14 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                {post.coverImage ? <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#0A1628] leading-snug line-clamp-2 group-hover:text-violet-700 transition-colors">
                  {post.title}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">{post.readTime} 分鐘 · {post.category}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h4 className="text-sm font-bold text-[#0A1628] mb-4">熱門標籤</h4>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <button
              key={tag}
              onClick={() => navigate(`/blog?search=${encodeURIComponent(tag)}`)}
              className="px-2.5 py-1 bg-slate-100 hover:bg-violet-50 hover:text-violet-700 text-slate-600 text-xs rounded-full cursor-pointer transition-colors whitespace-nowrap"
            >
              #{tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogDetailSidebar;
